import React, { useEffect, useState } from 'react';
import { FiUser, FiMail, FiPhone, FiMapPin, FiAlertTriangle, FiClock, FiHash, FiFileText, FiChevronDown } from 'react-icons/fi';
import InsuredTags from './InsuredTags';
import '../../styles/InsuredDetail.css';

interface InsuredData {
  databaseId: string;
  firstName: string | null;
  lastName: string | null;
  commercialName: string | null;
  email: string | null;
  phone: string | null;
  address1: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  changeDate: string | null;
  type: string | null;
}

interface PolicyData {
  databaseId: string | null;
  insuredDatabaseId: string;
  number: string | null;
  carrierName: string | null;
  linesOfBusiness: string[] | null;
  status: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  totalPremium: number | null;
  description: string | null;
  isQuote: boolean;
}

const PAGE_SIZE = 5;

// Loose types — NowCerts OData field names vary; we access defensively
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DriverRecord  = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VehicleRecord = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BoatRecord = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NoteRecord = Record<string, any>;

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseListResponse<T>(json: any, key: string): { items: T[]; total: number } {
  const items = Array.isArray(json?.[key]) ? (json[key] as T[]) : [];
  const total = typeof json?.total === 'number' ? json.total : items.length;
  return { items, total };
}

const InsuredDetailSidebarPanel: React.FC<{ insuredId: string }> = ({ insuredId }) => {
  const [data, setData] = useState<InsuredData | null>(null);
  const [policies, setPolicies]     = useState<PolicyData[]>([]);
  const [drivers,  setDrivers]      = useState<DriverRecord[]>([]);
  const [vehicles, setVehicles]     = useState<VehicleRecord[]>([]);
  const [boats,    setBoats]        = useState<BoatRecord[]>([]);
  const [notes,    setNotes]        = useState<NoteRecord[]>([]);
  // Notes the user opened to read in full (list index); cleared when the list reloads.
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [loading,  setLoading]      = useState(true);
  const [polLoad,  setPolLoad]      = useState(true);
  const [drvLoad,  setDrvLoad]      = useState(true);
  const [vehLoad,  setVehLoad]      = useState(true);
  const [boatLoad, setBoatLoad]     = useState(true);
  const [noteLoad, setNoteLoad]     = useState(true);
  const [addingNote, setAddingNote] = useState(false);
  const [noteSubject, setNoteSubject] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError]   = useState<string | null>(null);
  const [error,    setError]        = useState<string | null>(null);
  const [activeTab, setActiveTab]   = useState<'policies' | 'drivers' | 'vehicles' | 'boats'>('policies');
  const [polFilter, setPolFilter]   = useState<'all' | 'active' | 'expired'>('all');

  // Server-side "Show More" pagination (skip/top) for Policies/Drivers/Vehicles/Boats.
  const [polTotal,  setPolTotal]    = useState(0);
  const [polSkip,   setPolSkip]     = useState(0);
  const [polMoreLoad, setPolMoreLoad] = useState(false);
  const [drvTotal,  setDrvTotal]    = useState(0);
  const [drvSkip,   setDrvSkip]     = useState(0);
  const [drvMoreLoad, setDrvMoreLoad] = useState(false);
  const [vehTotal,  setVehTotal]    = useState(0);
  const [vehSkip,   setVehSkip]     = useState(0);
  const [vehMoreLoad, setVehMoreLoad] = useState(false);
  const [boatTotal, setBoatTotal]   = useState(0);
  const [boatSkip,  setBoatSkip]    = useState(0);
  const [boatMoreLoad, setBoatMoreLoad] = useState(false);
  const [noteTotal, setNoteTotal]   = useState(0);
  const [noteSkip,  setNoteSkip]    = useState(0);
  const [noteMoreLoad, setNoteMoreLoad] = useState(false);

  useEffect(() => {
    if (!insuredId) return;
    // Each section shows as soon as its own call returns; responses for a
    // previously opened insured are dropped.
    let cancelled = false;
    setLoading(true);
    setPolLoad(true);
    setDrvLoad(true);
    setVehLoad(true);
    setBoatLoad(true);
    setNoteLoad(true);
    setError(null);
    setData(null);
    setPolicies([]);
    setPolTotal(0); setPolSkip(0);
    setDrivers([]);
    setDrvTotal(0); setDrvSkip(0);
    setVehicles([]);
    setVehTotal(0); setVehSkip(0);
    setBoats([]);
    setBoatTotal(0); setBoatSkip(0);
    setNotes([]);
    setExpandedNotes(new Set());
    setNoteTotal(0); setNoteSkip(0);

    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';
    const headers   = { Authorization: `Bearer ${authToken}` };
    const ncHeaders = { Authorization: `Bearer ${authToken}`, NCToken: amsToken };

    // Insured info (critical — show error if fails). The header renders as soon
    // as this returns; it doesn't wait for policies.
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insuredandprospectListById?insuredDatabaseId=${encodeURIComponent(insuredId)}&nowCertsToken=${encodeURIComponent(amsToken)}`,
      { headers }
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        const item = Array.isArray(json) ? json[0] : json;
        if (!item) throw new Error('No data found');
        return item as InsuredData;
      })
      .then((insured) => { if (!cancelled) setData(insured); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load insured details'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Policies (non-critical — fail silently, own loader on the Policies tab)
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/policies?skip=0&top=${PAGE_SIZE}`,
      { headers: ncHeaders }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (cancelled) return;
        const { items, total } = parseListResponse<PolicyData>(json, 'policies');
        setPolicies(items);
        setPolTotal(total);
        setPolSkip(items.length);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPolLoad(false); });

    // Drivers (non-critical — fail silently)
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/drivers?skip=0&top=${PAGE_SIZE}`,
      { headers: ncHeaders }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (cancelled) return;
        const { items, total } = parseListResponse<DriverRecord>(json, 'drivers');
        setDrivers(items);
        setDrvTotal(total);
        setDrvSkip(items.length);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDrvLoad(false); });

    // Vehicles (non-critical — fail silently)
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/vehicles?skip=0&top=${PAGE_SIZE}`,
      { headers: ncHeaders }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (cancelled) return;
        const { items, total } = parseListResponse<VehicleRecord>(json, 'vehicles');
        setVehicles(items);
        setVehTotal(total);
        setVehSkip(items.length);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setVehLoad(false); });

    // Boats (non-critical — fail silently)
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/boats?skip=0&top=${PAGE_SIZE}`,
      { headers: ncHeaders }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (cancelled) return;
        const { items, total } = parseListResponse<BoatRecord>(json, 'boats');
        setBoats(items);
        setBoatTotal(total);
        setBoatSkip(items.length);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setBoatLoad(false); });

    // Notes (non-critical — fail silently). Insured-level: no policyDatabaseId filter.
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/notes?skip=0&top=${PAGE_SIZE}`,
      { headers: ncHeaders }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (cancelled) return;
        const { items, total } = parseListResponse<NoteRecord>(json, 'notes');
        setNotes(items);
        setNoteTotal(total);
        setNoteSkip(items.length);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setNoteLoad(false); });

    return () => { cancelled = true; };
  }, [insuredId]);

  const handleAddNote = () => {
    const subject = noteSubject.trim();
    if (!subject || noteSaving) return;

    setNoteSaving(true);
    setNoteError(null);

    const authToken = localStorage.getItem('authToken') || '';
    const amsToken  = localStorage.getItem('amsToken')  || '';

    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/notes`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          NCToken: amsToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subject }),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        setNotes(prev => [{ subject, changeDate: new Date().toISOString() }, ...prev]);
        setExpandedNotes(new Set());
        setNoteTotal(prev => prev + 1);
        setNoteSkip(prev => prev + 1);
        setNoteSubject('');
        setAddingNote(false);
      })
      .catch(() => setNoteError('Failed to save note.'))
      .finally(() => setNoteSaving(false));
  };

  const ncHeadersNow = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    NCToken: localStorage.getItem('amsToken') || '',
  });

  const handleLoadMorePolicies = () => {
    if (polMoreLoad || policies.length >= polTotal) return;
    setPolMoreLoad(true);
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/policies?skip=${polSkip}&top=${PAGE_SIZE}`,
      { headers: ncHeadersNow() }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const { items, total } = parseListResponse<PolicyData>(json, 'policies');
        setPolicies(prev => [...prev, ...items]);
        setPolTotal(total);
        setPolSkip(prev => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setPolMoreLoad(false));
  };

  const handleLoadMoreDrivers = () => {
    if (drvMoreLoad || drivers.length >= drvTotal) return;
    setDrvMoreLoad(true);
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/drivers?skip=${drvSkip}&top=${PAGE_SIZE}`,
      { headers: ncHeadersNow() }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const { items, total } = parseListResponse<DriverRecord>(json, 'drivers');
        setDrivers(prev => [...prev, ...items]);
        setDrvTotal(total);
        setDrvSkip(prev => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setDrvMoreLoad(false));
  };

  const handleLoadMoreVehicles = () => {
    if (vehMoreLoad || vehicles.length >= vehTotal) return;
    setVehMoreLoad(true);
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/vehicles?skip=${vehSkip}&top=${PAGE_SIZE}`,
      { headers: ncHeadersNow() }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const { items, total } = parseListResponse<VehicleRecord>(json, 'vehicles');
        setVehicles(prev => [...prev, ...items]);
        setVehTotal(total);
        setVehSkip(prev => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setVehMoreLoad(false));
  };

  const handleLoadMoreBoats = () => {
    if (boatMoreLoad || boats.length >= boatTotal) return;
    setBoatMoreLoad(true);
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/boats?skip=${boatSkip}&top=${PAGE_SIZE}`,
      { headers: ncHeadersNow() }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const { items, total } = parseListResponse<BoatRecord>(json, 'boats');
        setBoats(prev => [...prev, ...items]);
        setBoatTotal(total);
        setBoatSkip(prev => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setBoatMoreLoad(false));
  };

  const handleLoadMoreNotes = () => {
    if (noteMoreLoad || notes.length >= noteTotal) return;
    setNoteMoreLoad(true);
    fetch(
      `${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/ampnowcerts/insured/${encodeURIComponent(insuredId)}/notes?skip=${noteSkip}&top=${PAGE_SIZE}`,
      { headers: ncHeadersNow() }
    )
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const { items, total } = parseListResponse<NoteRecord>(json, 'notes');
        setNotes(prev => [...prev, ...items]);
        setNoteTotal(total);
        setNoteSkip(prev => prev + items.length);
      })
      .catch(() => {})
      .finally(() => setNoteMoreLoad(false));
  };

  if (loading) {
    return (
      <div className="isp-skeleton">
        <div className="isp-skeleton__card">

          {/* Hero */}
          <div className="isp-skeleton__hero">
            <div className="isp-skeleton__avatar dw-skel dw-skel--circle isp-skeleton__avatar-dim" />
            <div className="isp-skeleton__hero-text">
              <div className="dw-skel isp-skeleton__line--hero-lg" />
              <div className="dw-skel isp-skeleton__line--hero-sm" />
              <div className="dw-skel isp-skeleton__line--hero-badge" />
            </div>
          </div>

          <div className="isp-skeleton__divider" />

          {/* Contact */}
          <div className="isp-skeleton__group">
            <div className="dw-skel dw-skel--sm isp-skeleton__label" />
            <div className="isp-skeleton__row">
              <div className="dw-skel isp-skeleton__icon" />
              <div className="dw-skel dw-skel--sm isp-skeleton__line--w72" />
            </div>
            <div className="isp-skeleton__row">
              <div className="dw-skel isp-skeleton__icon" />
              <div className="dw-skel dw-skel--sm isp-skeleton__line--w48" />
            </div>
          </div>

          <div className="isp-skeleton__divider" />

          {/* Address */}
          <div className="isp-skeleton__group">
            <div className="dw-skel dw-skel--sm isp-skeleton__label" />
            <div className="isp-skeleton__row">
              <div className="dw-skel isp-skeleton__icon" />
              <div className="dw-skel dw-skel--sm isp-skeleton__line--w78" />
            </div>
          </div>

          <div className="isp-skeleton__divider" />

          {/* Details */}
          <div className="isp-skeleton__group">
            <div className="dw-skel dw-skel--sm isp-skeleton__label" />
            <div className="isp-skeleton__row isp-skeleton__row--top">
              <div className="dw-skel isp-skeleton__icon" />
              <div className="isp-skeleton__detail-col">
                <div className="dw-skel dw-skel--sm isp-skeleton__line--w38" />
                <div className="dw-skel dw-skel--sm isp-skeleton__line--w55" />
              </div>
            </div>
            <div className="isp-skeleton__row isp-skeleton__row--top">
              <div className="dw-skel isp-skeleton__icon" />
              <div className="isp-skeleton__detail-col">
                <div className="dw-skel dw-skel--sm isp-skeleton__line--w34" />
                <div className="dw-skel dw-skel--sm isp-skeleton__line--w92" />
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="isp__state isp__state--error">
        <div className="isp__state-icon">
          <FiAlertTriangle size={18} />
        </div>
        <span className="isp__state-text">{error}</span>
      </div>
    );
  }

  if (!data) return null;

  // Safely build strings — treat "null" strings and actual nulls the same
  const clean = (v: string | null | undefined) =>
    !v || v === 'null' || v.trim() === '' ? '' : v.trim();

  const firstName = clean(data.firstName);
  const lastName = clean(data.lastName);
  const commercialName = clean(data.commercialName);

  const displayName = commercialName || [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  const contactName = commercialName && (firstName || lastName)
    ? [firstName, lastName].filter(Boolean).join(' ')
    : '';
  const initials = displayName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const email = clean(data.email);
  const phone = clean(data.phone);
  const address = [
    clean(data.address1),
    clean(data.city),
    clean(data.state),
    clean(data.zipCode),
  ].filter(Boolean).join(', ');

  return (
    <>
    <div className="isp">
      <div className="isp__card">

        {/* ── Avatar + Name ── */}
        <div className="isp__hero">
          <div className="isp__hero-glow" />
          <div className="isp__avatar">{initials || <FiUser size={18} />}</div>
          <div className="isp__hero-text">
            <span className="isp__name">{displayName}</span>
            {contactName && <span className="isp__sub">{contactName}</span>}
            <span className={`isp__badge ${data.type == 'Prospect' ? 'isp__badge--prospect' : 'isp__badge--insured'}`}>
              {data.type == 'Prospect' ? 'Prospect' : 'Insured'}
            </span>
          </div>
          <a
            href={`${localStorage.getItem('agencyDomain')}/AMSINS/${data.type=='Prospect' ? 'Prospects' : 'Insureds'}/Details/${data.databaseId}/Information`}
            target="_blank"
            rel="noopener noreferrer"
            className="isp__hero-redirect"
            title="View in NowCerts"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>

        <InsuredTags insuredId={data.databaseId || insuredId} />

        <div className="isp__divider" />

        {/* ── Contact ── */}
        {(email || phone) && (
          <div className="isp__group">
            <span className="isp__group-label">Contact</span>
            {email && (
              <a href={`mailto:${email}`} className="isp__row">
                <span className="isp__row-icon isp__row-icon--cyan"><FiMail size={12} /></span>
                <span className="isp__row-text">{email}</span>
              </a>
            )}
            {phone && (
              <a href={`tel:${phone}`} className="isp__row">
                <span className="isp__row-icon isp__row-icon--cyan"><FiPhone size={12} /></span>
                <span className="isp__row-text">{phone}</span>
              </a>
            )}
          </div>
        )}

        {/* ── Address ── */}
        {address && (
          <>
            <div className="isp__divider" />
            <div className="isp__group">
              <span className="isp__group-label">Address</span>
              <div className="isp__row isp__row--static">
                <span className="isp__row-icon isp__row-icon--purple"><FiMapPin size={12} /></span>
                <span className="isp__row-text">{address}</span>
              </div>
            </div>
          </>
        )}

        <div className="isp__divider" />

        {/* ── Details ── */}
        <div className="isp__group">
          <span className="isp__group-label">Details</span>

          {data.changeDate && (
            <div className="isp__row isp__row--static">
              <span className="isp__row-icon"><FiClock size={12} /></span>
              <div className="isp__row-kv">
                <span className="isp__kv-label">Last Updated</span>
                <span className="isp__kv-value">
                  {new Date(data.changeDate).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric',
                  })}
                </span>
              </div>
            </div>
          )}

          <div className="isp__row isp__row--static">
            <span className="isp__row-icon"><FiHash size={12} /></span>
            <div className="isp__row-kv">
              <span className="isp__kv-label">Database ID</span>
              <span className="isp__kv-value isp__kv-value--mono">{data.databaseId}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

    {/* ── Tabbed card: Policies / Drivers / Vehicles ── */}
    <div className="isp isp--tabs-card">
      <div className="isp__card">

        {/* Tab bar */}
        <div className="isp__tabs">
          {([
            { key: 'policies', label: 'Policies', count: polTotal,  loading: polLoad  },
            { key: 'drivers',  label: 'Drivers',  count: drvTotal,  loading: drvLoad  },
            { key: 'vehicles', label: 'Vehicles', count: vehTotal,  loading: vehLoad  },
            { key: 'boats',    label: 'Boats',    count: boatTotal, loading: boatLoad },
          ] as const).map(t => (
            <button
              key={t.key}
              className={`isp__tab${activeTab === t.key ? ' isp__tab--active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
              {t.loading
                ? <span className="isp__tab-spin" />
                : <span className="isp__tab-count">{t.count}</span>}
            </button>
          ))}
        </div>

        <div className="isp__divider" />

        {/* ── Policies tab ── */}
        {activeTab === 'policies' && (() => {
          const fmtDate = (d: string | null) =>
            d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
          // Filter applies to whatever's loaded so far — "Show More" fetches
          // the next raw page from NowCerts (unfiltered), same tradeoff as
          // Drivers/Vehicles/Boats below.
          const filtered = polFilter === 'all' ? policies : policies.filter(p => (p.status || '').toLowerCase() === polFilter);
          return (
            <>
              <div className="isp__pol-toolbar">
                <div className="isp__pol-filters">
                  {(['all', 'active', 'expired'] as const).map(f => (
                    <button key={f}
                      className={`isp__pol-filter${polFilter === f ? ' isp__pol-filter--active' : ''} isp__pol-filter--${f}`}
                      onClick={() => setPolFilter(f)}>
                      {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="isp__group">
                {polLoad
                  ? [0,1,2].map(i => <div key={i} className="isp__list-skel" style={{ width: `${75 - i * 10}%` }} />)
                  : filtered.length === 0
                  ? <p className="isp__empty">No policies found.</p>
                  : filtered.map((pol, idx) => {
                    const isActive = (pol.status || '').toLowerCase() === 'active';
                    // NowCerts OData returns `lineOfBusinesses` as objects; defensively
                    // also accept a flat string-array shape if it ever appears.
                    const lobList: string[] = (
                      (pol as any).lineOfBusinesses || pol.linesOfBusiness || []
                    ).map((lob: any) => (typeof lob === 'string' ? lob : lob?.lineOfBusinessName))
                     .filter(Boolean);
                    return (
                      <div key={pol.databaseId || idx} className="isp__policy-item">
                        <div className="isp__policy-header">
                          <span className="isp__row-icon isp__row-icon--blue"><FiFileText size={12} /></span>
                          <div className="isp__policy-meta">
                            <span className="isp__policy-carrier">{pol.number || `Policy ${idx + 1}`}</span>
                            {pol.carrierName && <span className="isp__kv-value--mono isp__policy-num">{pol.carrierName}</span>}
                          </div>
                          <span className={`isp__badge ${isActive ? 'isp__badge--insured' : 'isp__badge--expired'}`}>{pol.status || '—'}</span>
                        </div>
                        <div className="isp__policy-dates">
                          <span>{fmtDate(pol.effectiveDate)}</span>
                          <span className="isp__policy-dates-sep">→</span>
                          <span>{fmtDate(pol.expirationDate)}</span>
                        </div>
                        {lobList.length > 0 && (
                          <div className="isp__policy-lobs">
                            {lobList.map((lob, li) => (
                              <span key={li} className="isp__policy-lob-chip">{lob}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              {!polLoad && policies.length < polTotal && (
                <button className="isp__show-more-btn" onClick={handleLoadMorePolicies} disabled={polMoreLoad}>
                  {polMoreLoad ? 'Loading…' : `Show More (${policies.length} of ${polTotal})`}
                </button>
              )}
            </>
          );
        })()}

        {/* ── Drivers tab ── */}
        {activeTab === 'drivers' && (
          <div className="isp__group">
            {drvLoad
              ? [0,1,2].map(i => <div key={i} className="isp__list-skel" style={{ width: `${75 - i * 10}%` }} />)
              : drivers.length === 0
                ? <p className="isp__empty">No drivers found.</p>
                : drivers.map((d, i) => {
                    const firstName    = d.firstName || d.first_name || '';
                    const lastName     = d.lastName  || d.last_name  || '';
                    const name         = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
                    const initials     = name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
                    const license      = d.licenseNumber || d.license_number || d.driverLicenseNumber || '';
                    const dob          = d.dateOfBirth || d.date_of_birth || '';
                    const gender       = d.gender || '';
                    const state        = d.licenseState || d.license_state || '';
                    // Separate driving status (active/inactive/excluded) from marital status
                    const driverStatus  = d.driverStatus || d.status || '';
                    const maritalStatus = d.maritalStatus || '';
                    const dStatusLow    = driverStatus.toLowerCase();
                    const dStatusClass  = dStatusLow === 'active'
                      ? 'isp__badge--insured'
                      : dStatusLow === 'excluded' || dStatusLow === 'inactive'
                        ? 'isp__badge--expired'
                        : 'isp__badge--neutral';
                    return (
                      <div key={i} className="isp__list-item">
                        <div className="isp__list-avatar isp__list-avatar--driver">{initials || <FiUser size={11} />}</div>
                        <div className="isp__list-meta">
                          <span className="isp__list-title">{name}</span>
                          <div className="isp__list-details">
                            {license && <span className="isp__list-chip isp__list-chip--license">#{license}</span>}
                            {state   && <span className="isp__list-chip isp__list-chip--state">{state}</span>}
                            {dob     && <span className="isp__list-muted">{new Date(dob).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
                            {gender  && <span className="isp__list-muted">{gender}</span>}
                            {maritalStatus && <span className="isp__list-chip isp__list-chip--marital">{maritalStatus}</span>}
                          </div>
                        </div>
                        {driverStatus && <span className={`isp__badge ${dStatusClass}`}>{driverStatus}</span>}
                      </div>
                    );
                  })
            }
          </div>
        )}
        {activeTab === 'drivers' && !drvLoad && drivers.length < drvTotal && (
          <button className="isp__show-more-btn" onClick={handleLoadMoreDrivers} disabled={drvMoreLoad}>
            {drvMoreLoad ? 'Loading…' : `Show More (${drivers.length} of ${drvTotal})`}
          </button>
        )}

        {/* ── Vehicles tab ── */}
        {activeTab === 'vehicles' && (
          <div className="isp__group">
            {vehLoad
              ? [0,1,2].map(i => <div key={i} className="isp__list-skel" style={{ width: `${75 - i * 10}%` }} />)
              : vehicles.length === 0
                ? <p className="isp__empty">No vehicles found.</p>
                : vehicles.map((v, i) => {
                    const make  = v.make  || '';
                    const model = v.model || '';
                    const year  = v.modelYear || v.year || v.vehicleYear || '';
                    const vin   = v.vin   || v.vehicleIdentificationNumber || '';
                    const type  = v.vehicleType || v.type || '';
                    const use   = v.use   || v.vehicleUse || '';
                    const label = [year, make, model].filter(Boolean).join(' ') || 'Unknown Vehicle';
                    return (
                      <div key={i} className="isp__list-item">
                        <div className="isp__list-avatar isp__list-avatar--vehicle">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                          </svg>
                        </div>
                        <div className="isp__list-meta">
                          <span className="isp__list-title">{label}</span>
                          <div className="isp__list-details">
                            {vin  && <span className="isp__list-chip isp__list-chip--vin" title="VIN">{vin.length > 14 ? vin.slice(0, 14) + '…' : vin}</span>}
                            {type && <span className="isp__list-chip isp__list-chip--type">{type}</span>}
                            {use  && <span className="isp__list-chip isp__list-chip--use">{use}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
            }
          </div>
        )}
        {activeTab === 'vehicles' && !vehLoad && vehicles.length < vehTotal && (
          <button className="isp__show-more-btn" onClick={handleLoadMoreVehicles} disabled={vehMoreLoad}>
            {vehMoreLoad ? 'Loading…' : `Show More (${vehicles.length} of ${vehTotal})`}
          </button>
        )}

        {activeTab === 'boats' && (
          <div className="isp__group">
            {boatLoad
              ? [0,1,2].map(i => <div key={i} className="isp__list-skel" style={{ width: `${75 - i * 10}%` }} />)
              : boats.length === 0
                ? <p className="isp__empty">No boats found.</p>
                : boats.map((b, i) => {
                    const make  = b.make  || '';
                    const model = b.model || '';
                    const year  = b.modelYear || b.year || b.boatYear || '';
                    const hin   = b.hin   || b.hullIdentificationNumber || '';
                    const type  = b.watercraftType || b.type || '';
                    const length = b.length || b.boatLength || '';
                    const label = [year, make, model].filter(Boolean).join(' ') || 'Unknown Boat';
                    return (
                      <div key={i} className="isp__list-item">
                        <div className="isp__list-avatar isp__list-avatar--vehicle">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 20h20"/><path d="M4 20l1.5-6.5a2 2 0 0 1 2-1.5h9a2 2 0 0 1 2 1.5L20 20"/><path d="M12 12V4l4 3"/>
                          </svg>
                        </div>
                        <div className="isp__list-meta">
                          <span className="isp__list-title">{label}</span>
                          <div className="isp__list-details">
                            {hin    && <span className="isp__list-chip isp__list-chip--vin" title="HIN">{hin.length > 14 ? hin.slice(0, 14) + '…' : hin}</span>}
                            {type   && <span className="isp__list-chip isp__list-chip--type">{type}</span>}
                            {length && <span className="isp__list-chip isp__list-chip--use">{length} ft</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
            }
          </div>
        )}
        {activeTab === 'boats' && !boatLoad && boats.length < boatTotal && (
          <button className="isp__show-more-btn" onClick={handleLoadMoreBoats} disabled={boatMoreLoad}>
            {boatMoreLoad ? 'Loading…' : `Show More (${boats.length} of ${boatTotal})`}
          </button>
        )}

      </div>
    </div>

    {/* ── Notes section (insured-level, below the tabbed card) ── */}
    <div className="isp isp--tabs-card">
      <div className="isp__card">
        <div className="isp__group">
          <div className="isp__group-header">
            <span className="isp__group-label">Notes</span>
            {!addingNote && (
              <button className="isp__add-note-btn" onClick={() => { setAddingNote(true); setNoteError(null); }}>
                + Add Note
              </button>
            )}
          </div>

          {addingNote && (
            <div className="isp__note-form">
              <input
                className="isp__note-input"
                placeholder="Note subject…"
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                autoFocus
              />
              {noteError && <p className="isp__note-error">{noteError}</p>}
              <div className="isp__note-actions">
                <button
                  className="isp__note-cancel-btn"
                  onClick={() => { setAddingNote(false); setNoteSubject(''); setNoteError(null); }}
                  disabled={noteSaving}
                >
                  Cancel
                </button>
                <button
                  className="isp__note-save-btn"
                  onClick={handleAddNote}
                  disabled={noteSaving || !noteSubject.trim()}
                >
                  {noteSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {noteLoad
            ? [0,1,2].map(i => <div key={i} className="isp__list-skel" style={{ width: `${75 - i * 10}%` }} />)
            : notes.length === 0
              ? <p className="isp__empty">No notes found.</p>
              : notes.map((n, i) => {
                  const subject = stripHtml(n.subject || '') || 'Untitled Note';
                  const text = stripHtml(n.note || n.description || n.notesDescription || n.text || '');
                  const date = n.changeDate || n.createdDate || n.date || '';
                  const author = n.createdBy || n.userName || n.author || '';
                  // Subject and text are cut short in the list — clicking a note
                  // opens it in place to read the whole thing.
                  const expanded = expandedNotes.has(i);
                  // Many notes keep the whole note in the subject. Only show it as a
                  // heading when it is a short title over a separate body.
                  const showHeading = !!text && text !== subject && subject.length <= 80;
                  const fullText = showHeading ? text : (text && text !== subject ? `${subject}\n\n${text}` : subject);
                  const toggle = () => setExpandedNotes(prev => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i); else next.add(i);
                    return next;
                  });
                  return (
                    <div
                      key={i}
                      className={`isp__list-item isp__list-item--note${expanded ? ' isp__list-item--expanded' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={expanded}
                      title={expanded ? 'Click to collapse' : 'Click to read the full note'}
                      onClick={toggle}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                    >
                      <div className="isp__list-avatar isp__list-avatar--vehicle">
                        <FiFileText size={13} />
                      </div>
                      <div className="isp__list-meta">
                        {expanded ? (
                          <div className="isp__note-full">
                            {showHeading && <span className="isp__note-full-heading">{subject}</span>}
                            <p className="isp__note-full-text">{fullText}</p>
                          </div>
                        ) : (
                          <>
                            <span className="isp__list-title">{subject}</span>
                            {text && <div className="isp__list-sub">{text}</div>}
                          </>
                        )}
                        <div className="isp__list-details">
                          {author && <span className="isp__list-chip isp__list-chip--type">{author}</span>}
                          {date   && <span className="isp__list-chip isp__list-chip--use">{new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
                          <span className="isp__note-toggle">
                            {expanded ? 'Show less' : 'Read more'}
                            <FiChevronDown size={12} />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
          }
          {!noteLoad && notes.length < noteTotal && (
            <button className="isp__show-more-btn" onClick={handleLoadMoreNotes} disabled={noteMoreLoad}>
              {noteMoreLoad ? 'Loading…' : `Show More (${notes.length} of ${noteTotal})`}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default InsuredDetailSidebarPanel;

